import { useForm } from 'react-hook-form'
import { UserRole } from '@magnetar/admin-api-interfaces'
import { userRoleService } from '@/services/user_role_service'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'

type FormValues = {
  role_name: string
  role_description: string | null
}

interface UserRoleSettingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  role?: UserRole // If provided, we're in edit mode
  onSuccess: () => void // Callback to refresh the data
}

export function UserRoleSettingDialog({
  open,
  onOpenChange,
  role,
  onSuccess,
}: UserRoleSettingDialogProps) {
  const { token } = useAuth()
  const { mode, id } = useViewMode()
  const isEditMode = !!role

  const form = useForm<FormValues>({
    defaultValues: {
      role_name: role?.role_name || '',
      role_description: role?.role_description || '',
    },
  })

  // 判斷是否為 provider mode
  const isProviderMode = mode === 'provider'

  const onSubmit = async (values: FormValues) => {
    try {
      // 根據 viewMode 決定 scope_type 和相關 ID
      const payload: {
        role_name: string
        role_description: string | null
        role_type?: string
        scope_type?: 'sys_admin' | 'provider'
        provider_id?: number | null
      } = {
        role_name: values.role_name,
        role_description: values.role_description || null,
        role_type: 'custom', // 永遠使用 custom 類型
      }

      // 如果在 provider mode 下，設定 scope_type 和 provider_id
      if (isProviderMode && id) {
        payload.scope_type = 'provider'
        payload.provider_id = id
      }

      let result
      if (isEditMode) {
        result = await userRoleService.updateUserRole(role, payload, token)
      } else {
        result = await userRoleService.createUserRole(payload, token)
      }

      if (result.success) {
        toast.success(`${isEditMode ? '更新' : '新增'}角色成功`)
        form.reset()
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(`${isEditMode ? '更新' : '新增'}角色失敗`)
      }
    } catch {
      toast.error(`${isEditMode ? '更新' : '新增'}角色失敗`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode
              ? isProviderMode
                ? '編輯組織角色'
                : '編輯角色'
              : isProviderMode
                ? '新增組織角色'
                : '新增角色'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? isProviderMode
                ? '修改組織角色的基本資料'
                : '修改角色的基本資料'
              : isProviderMode
                ? '建立一個新的組織層級角色'
                : '建立一個新的角色'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="role_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色名稱</FormLabel>
                  <FormControl>
                    <Input placeholder="輸入角色名稱" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>角色描述</FormLabel>
                  <FormControl>
                    <Textarea placeholder="輸入角色描述" className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button type="submit">{isEditMode ? '更新' : '建立'}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
